import { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { userGroupsService } from '@modules/userGroups/services/userGroupsService';
import { userService } from '../services/userService';
import { notifySuccess, notifyError } from '@config/tostifyConfig/tostifyConfig';
import useFormHook from '@shared/hooks/useFormHook';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';

const getDefaultFormValues = () => ({
    username: '',
    description: '',
    employeeNumber: '',
    personId: '',
    groupId: '',
    password: '',
    confirmPassword: '',
    ldapAccess: false,
    isLocked: false,
    extendedAccess: false,
    enabled: true,
    accountNonExpired: true,
    credentialsNonExpired: true,
    accountNonLocked: true,
    deleted: false,
});

export const useUsersForm = ({ userId, onClose, selectedData, fetchData }) => {
    const { t, i18n } = useTranslation();
    const { modalRef } = useFormHook({}, onClose);
    const isEdit = Boolean(userId);
    const [serverMessage, setServerMessage] = useState(null);
    const [selectedGroup, setSelectedGroup] = useState('');
    const [selectedPerson, setSelectedPerson] = useState('');
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(isEdit);
    const [passwordType, setPasswordType] = useState('password');
    const [confirmPasswordType, setConfirmPasswordType] = useState('password');
    const [passwordIcon, setPasswordIcon] = useState(faEye);
    const [confirmPasswordIcon, setConfirmPasswordIcon] = useState(faEye);

    const validationSchema = useCallback(() => {
        return yup.object({
            username: yup.string()
                .required(t('validation.username.notEmpty'))
                .min(3, t('validation.user.username.size'))
                .max(30, t('validation.user.username.size'))
                .matches(/\S/, t('validation.username.notEmpty')),
            password: yup.string()
                .nullable()
                .when('$isEdit', {
                    is: false,
                    then: (schema) => schema
                        .required(t('validation.user.password.required'))
                        .min(3, t('validation.user.password.size'))
                        .max(60, t('validation.user.password.size')),
                    otherwise: (schema) => schema
                        .test('password-length', t('validation.user.password.size'), function(value) {
                            if (!value || value.trim() === '') {
                                return true;
                            }
                            return value.length >= 3 && value.length <= 60;
                        })
                }),
            confirmPassword: yup.string()
                .nullable()
                .when('password', {
                    is: (value) => value && value.trim().length > 0,
                    then: (schema) => schema.oneOf([yup.ref('password')], t('validation.user.password.match')),
                    otherwise: (schema) => schema.nullable()
                }),
            personId: yup.string()
                .required(t('common.required')),
            groupId: yup.string()
                .required(t('common.required')),
        });
    }, [t]);

    const { register, handleSubmit, setValue, reset, formState: { errors }, trigger } = useForm({
        resolver: yupResolver(validationSchema()),
        mode: 'onChange',
        context: { isEdit },
        defaultValues: getDefaultFormValues(),
    });

    useEffect(() => {
        if (Object.keys(errors).length > 0) {
            trigger();
        }
    }, [i18n.language, trigger]);

    useEffect(() => {
        let isMounted = true;

        const initializeForm = async () => {
            try {
                setLoading(isEdit);
                setServerMessage(null);

                const [groupsResponse, user] = await Promise.all([
                    userGroupsService.allUserGroups(),
                    isEdit ? userService.getUserById(userId) : Promise.resolve(null),
                ]);

                if (!isMounted) {
                    return;
                }

                const mappedGroups = Array.isArray(groupsResponse)
                    ? groupsResponse.map((group) => ({
                        value: group.id.toString(),
                        label: group.name,
                    }))
                    : [];
                setGroups(mappedGroups);

                if (isEdit && user) {
                    const userGroupId = Array.isArray(user.groups) ? user.groups[0] : null;
                    const matchedGroup = mappedGroups.find(
                        (group) => group.value === userGroupId?.toString()
                    );
                    const groupId = matchedGroup?.value || '';
                    const personId = user.personId?.toString() || '';

                    reset({
                        username: user.username || '',
                        description: user.description || '',
                        employeeNumber: user.empId ?? '',
                        personId,
                        groupId,
                        ldapAccess: user.ldapAccess ?? false,
                        isLocked: user.isLocked ?? false,
                        extendedAccess: user.extendedAccess ?? false,
                        password: '',
                        confirmPassword: '',
                    });
                    setSelectedGroup(groupId);
                    setSelectedPerson(personId);
                } else {
                    reset(getDefaultFormValues());
                    const defaultGroup = mappedGroups.find((group) => group.label === 'USER');
                    if (defaultGroup) {
                        setSelectedGroup(defaultGroup.value);
                        setValue('groupId', defaultGroup.value);
                    } else {
                        setSelectedGroup('');
                    }
                    setSelectedPerson('');
                }
            } catch (error) {
                console.error('Error initializing user form:', error);
                notifyError(t('common.errorMessage'));
                onClose();
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        initializeForm();

        return () => {
            isMounted = false;
        };
    }, [userId, isEdit, reset, setValue, onClose, t]);

    const handleUpdateUser = useCallback(async (formData) => {
        try {
            const dataToUpdate = { ...formData };
            if (!dataToUpdate.password?.trim()) {
                delete dataToUpdate.password;
            }

            await userService.updateUser(selectedData, dataToUpdate);
            notifySuccess(t('common.updatedSuccessfully'));
            return true;
        } catch (error) {
            console.error('Update error:', error);
            setServerMessage(error?.response?.data?.message);
            notifyError(error?.response?.data?.message || t('common.errorMessage'));
            return false;
        }
    }, [selectedData, t]);

    const handleCreateUser = useCallback(async (data) => {
        try {
            const response = await userService.createUser(data);
            notifySuccess(typeof response === 'string' ? response : t('common.createdSuccessfully'));
            return true;
        } catch (error) {
            console.error('Create error:', error);
            const message = error?.response?.data?.username || error?.response?.data?.message;
            setServerMessage(message);
            notifyError(message || t('common.errorMessage'));
            return false;
        }
    }, [t]);

    const onSubmit = useCallback(async (data) => {
        try {
            const groupId = data.groupId || selectedGroup;
            const payload = {
                ...data,
                groups: groupId ? [parseInt(groupId, 10)] : [],
            };
            delete payload.confirmPassword;
            delete payload.groupId;

            const success = isEdit
                ? await handleUpdateUser(payload)
                : await handleCreateUser(payload);

            if (success) {
                fetchData(userService.getUsers, {});
                onClose();
            }
        } catch (error) {
            console.error('Submission error:', error);
        }
    }, [isEdit, handleUpdateUser, handleCreateUser, fetchData, onClose, selectedGroup]);

    const generatePassword = useCallback(() => {
        const length = 12;
        const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
        let password = '';

        password += charset.match(/[A-Z]/)[0];
        password += charset.match(/[a-z]/)[0];
        password += charset.match(/[0-9]/)[0];
        password += charset.match(/[!@#$%^&*]/)[0];

        for (let i = password.length; i < length; i++) {
            password += charset.charAt(Math.floor(Math.random() * charset.length));
        }

        password = password.split('').sort(() => Math.random() - 0.5).join('');

        setValue('password', password);
        setValue('confirmPassword', password);
    }, [setValue]);

    const togglePasswordVisibility = () => {
        if (passwordType === 'password') {
            setPasswordType('text');
            setPasswordIcon(faEyeSlash);
        } else {
            setPasswordType('password');
            setPasswordIcon(faEye);
        }
    };

    const toggleConfirmPasswordVisibility = () => {
        if (confirmPasswordType === 'password') {
            setConfirmPasswordType('text');
            setConfirmPasswordIcon(faEyeSlash);
        } else {
            setConfirmPasswordType('password');
            setConfirmPasswordIcon(faEye);
        }
    };

    return {
        modalRef,
        register,
        handleSubmit,
        errors,
        setValue,
        serverMessage,
        setServerMessage,
        selectedGroup,
        setSelectedGroup,
        selectedPerson,
        setSelectedPerson,
        groups,
        onSubmit,
        generatePassword,
        passwordType,
        confirmPasswordType,
        passwordIcon,
        confirmPasswordIcon,
        togglePasswordVisibility,
        toggleConfirmPasswordVisibility,
        loading,
        isEdit,
    };
};
